import React, { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Typography, Paper, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Container, Grid, styled, tableCellClasses,
} from '@mui/material';
import axios from "axios";
import utils from "../../utils";
import { LoadingButton } from '@mui/lab';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#333333', // Sfondo scuro
    color: theme.palette.common.white, // Testo bianco
    fontWeight: 'bold', // Testo in grassetto
    fontSize: '1.1rem',
    textAlign: 'center',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '1rem',
    textAlign: 'center',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PrenotaCBA = () => {
  const [open, setOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isError, setError] = useState(true);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get('/api/richiestecba', {
        headers: utils.getAuthHeaders(),
      });
      const { success, error, requests } = res.data;
      if (success) {
        setRequests(requests);
      } else {
        setError(true);
        setStatusMessage(error);
      }
    } catch (error) {
      setError(true);
      setStatusMessage("Si è verificato un errore imprevisto sul nostro server.");
      console.log(error);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    setOpen(false);
    setStatusMessage("");
    setLoading(true);

    try {
      const res = await axios.post(`/api/prenotacba`, {},
        {
          headers: utils.getAuthHeaders(),
        }
      );
      const { success, error } = res.data;
      if (success) {
        setError(false);
        setStatusMessage("Richiesta inviata con successo.");
      } else {
        setError(true);
        setStatusMessage(error);
      }
    } catch (error) {
      setError(true);
      setStatusMessage(
        "Si è verificato un errore imprevisto sul nostro server."
      );
      console.log(error);
    }
    setLoading(false);
    loadRequests(); // Ricarica le richieste dopo l'invio di una nuova richiesta
  };

  const handleFileDownload = async (docId) => {
    try {
      await utils.cliDownload(docId);
    } catch (error) {
      setError('File non trovato');
      console.error('Errore nel download della busta paga:', error);
    }
  };

  const handleFileView = (docId) => {
    try {
      utils.cliOpenPdfInNewTab(docId);
    } catch (error) {
      setError('Errore nell\'anteprima del PDF: ' + error.message);
      console.error('Errore nell\'anteprima della busta paga:', error);
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" spacing={3}>
      <Container maxWidth="xl">
        <Paper
          sx={{
            p: 2,
            boxShadow: 3,
            backgroundColor: '#fff',
            borderRadius: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h4" gutterBottom>
                Prenota CBA
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom sx={{ fontStyle: 'italic', color: 'gray' }}>
                premi il bottone per prenotare una sessione di CBA ed essere ricontattato da un nostro operatore.
              </Typography>
            </Grid>
            <Grid item>
              <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleClickOpen}>
                Prenota
              </LoadingButton>
            </Grid>
          </Grid>

          {statusMessage && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 3,
                backgroundColor: isError ? "#e57373" : "#81c784",
                borderColor: isError ? "#d32f2f" : "#388e3c",
                color: "black",
              }}
              role="alert"
            >
              {statusMessage}
            </Paper>
          )}

          <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Conferma Prenotazione</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Sicuro di voler prenotare per essere ricontattato per una sessione CBA?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Annulla
              </Button>
              <Button onClick={handleConfirm} color="primary" autoFocus>
                Conferma
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>

      <Container maxWidth="xl">

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>Data Richiesta</StyledTableCell>
                <StyledTableCell>Documenti</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request, index) => (
                <StyledTableRow key={request.id}>
                  <StyledTableCell>{request.id}</StyledTableCell>
                  <StyledTableCell>{new Date(request.timestamp).toLocaleDateString()}</StyledTableCell>
                  <StyledTableCell>
                    {request.documento && (
                      <>
                        <IconButton onClick={() => handleFileDownload(request.documento)}>
                          <GetAppIcon />
                        </IconButton>
                        <IconButton onClick={() => handleFileView(request.documento)}>
                          <VisibilityIcon />
                        </IconButton>
                      </>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};

export default PrenotaCBA;
